import React, { useEffect, useState } from 'react'
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaHeartPulse, FaUtensils, FaBriefcaseMedical, FaCircleNodes, FaWifi, FaVideo, FaFileWaveform, FaUserGroup, FaHouseLaptop } from "react-icons/fa6";

import { startService, startGetModules } from "../../redux/slices/service";
import { startGetMensajes, startTipoMembresia } from '../../redux/slices/ui';

import { InfoMessage, WarningMessage } from '../../components/AlertMessages/AlertMessage';

import styles from './Services.module.css';

export const Services = () => {

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { membresia, membresiaActiva } = useSelector(state => state.auth);
  const { modulos } = useSelector(state => state.service);
  const { tipoMembresia, mensajes } = useSelector(state => state.ui);

  const [cargando, setCargando] = useState(true);
  const [moduloPendiente, setModuloPendiente] = useState(null);

  const moduloConfig = {
    'C': { icon: <FaHeartPulse className={styles.cardIcon} />, label: 'Control Vital' },
    'N': { icon: <FaUtensils className={styles.cardIcon} />, label: 'Nutrición' },
    'P': { icon: <FaCircleNodes className={styles.cardIcon} />, label: 'Psicología' },
    'M': { icon: <FaBriefcaseMedical className={styles.cardIcon} />, label: 'Médico' },
  };

  useEffect(() => {
    let mounted = true;

    const cargarDatos = async () => {
      setCargando(true);

      try {
        await Promise.allSettled([
          dispatch(startGetModules(membresia)),
          dispatch(startTipoMembresia(membresia)),
          dispatch(startGetMensajes(membresia)),
        ]);
      } finally {
        if (mounted) {
          setCargando(false);
        }
      }
    };

    if (membresia) {
      cargarDatos();
    } else {
      navigate("/", { replace: true });
    }

    return () => {
      mounted = false;
    };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCardClic = (idModuloConsulta, label) => {
    setModuloPendiente({ idModuloConsulta, label });
  };

  const handleConfirmar = async () => {
    const { idModuloConsulta } = moduloPendiente;
    setModuloPendiente(null);
    const res = await dispatch(startService(idModuloConsulta, membresia));
    if (res) navigate("/webcall");
  };

  const handleCancelar = () => {
    setModuloPendiente(null);
  };

  if (cargando) {
    return (
      <div className="container">
        <div className="row">
          <div className="col">
            <div className={styles.cargando}>
              <div className="spinner-border" role="status" style={{ width: '3rem', height: '3rem', color: 'var(--secondary-text)' }}>
                <span className="visually-hidden">Cargando...</span>
              </div>
              <div className={styles.cargandoTexto}>Cargando información...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className='container'>
        <div className="row">
          <div className="col">
            <div className={styles.header}>Área de atención</div>
            <div className={styles.text}>Selecciona el área del especialista con el que te quieres comunicar ahora mismo. Al dar clic te enlazaremos en una videollamada con uno de nuestros expertos para atender tu duda o inquietud.</div>
          </div>
        </div>
        <div className="row mt-5 mb-5">
          {!cargando && modulos.length === 0 && (
            <div className="col-12">
              <div className={styles.sinModulos}>
                <FaBriefcaseMedical className={styles.sinModulosIcono} />
                <div className={styles.sinModulosTexto}>
                  No cuentas con módulos de consulta disponibles en este momento.
                </div>
                <div className={styles.sinModulosSubtexto}>
                  Contacta a tu clínica para más información.
                </div>
              </div>
            </div>
          )}
          {modulos.map((mod) => {
            const config = moduloConfig[mod.key];
            if (!config) return null;
            return (
              <div key={mod.idModuloConsulta} className="col-sm-12 col-md-3">
                <div
                  className={`${styles.card}${!membresiaActiva ? ` ${styles.cardDisabled}` : ''}`}
                  onClick={membresiaActiva ? () => handleCardClic(mod.idModuloConsulta, config.label) : undefined}
                >
                  <div className={styles.cardIconContainer}>{config.icon}</div>
                  <div className={styles.cardHeader}>{config.label}</div>
                </div>
              </div>
            );
          })}
          {(tipoMembresia === 'FREE' && membresiaActiva) && (
            <div className="col-12">
              <InfoMessage text={mensajes.videollamadas} />
            </div>
          )}
          {!membresiaActiva && (
            <div className="col-12">
              <WarningMessage text="Su membresía ya no está activa" />
            </div>
          )}
          {moduloPendiente && (
            <div className={styles.modalOverlay} onClick={handleCancelar}>
              <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
                <div className={styles.modalTitulo}>Confirmar solicitud</div>
                <div className={styles.modalTexto}>
                  ¿Deseas iniciar una consulta de <strong>{moduloPendiente.label}</strong>?
                </div>
                <div className={styles.modalAcciones}>
                  <button className={styles.btnCancelar} onClick={handleCancelar}>Cancelar</button>
                  <button className={styles.btnConfirmar} onClick={handleConfirmar}>Continuar</button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="row">
          <div className="col">
            <div className={styles.infoHeader}>Tips para video consulta</div>
          </div>
        </div>
        <div className="row mb-5">
          <div className="col-sm-12 col-md">
            <div className={styles.cardIconContainer}><FaWifi className={styles.infoIcon} /></div>
            <div className={styles.infoSubheader}>Conexión estable</div>
            <div className={styles.infoText}>Asegúrate de tener un buen internet.</div>
          </div>
          <div className="col-sm-12 col-md">
            <div className={styles.cardIconContainer}><FaVideo className={styles.infoIcon} /></div>
            <div className={styles.infoSubheader}>Cámara y micrófono</div>
            <div className={styles.infoText}>Actívalos para que el especialista pueda verte y escucharte.</div>
          </div>
          <div className="col-sm-12 col-md">
            <div className={styles.cardIconContainer}><FaFileWaveform className={styles.infoIcon} /></div>
            <div className={styles.infoSubheader}>Documentos listos</div>
            <div className={styles.infoText}>Ten a la mano tus resultados y medicamentos.</div>
          </div>
          <div className="col-sm-12 col-md">
            <div className={styles.cardIconContainer}><FaHouseLaptop className={styles.infoIcon} /></div>
            <div className={styles.infoSubheader}>Espacio privado</div>
            <div className={styles.infoText}>Busca un lugar sin ruidos ni distracciones.</div>
          </div>
          <div className="col-sm-12 col-md">
            <div className={styles.cardIconContainer}><FaUserGroup className={styles.infoIcon} /></div>
            <div className={styles.infoSubheader}>Acompañante opcional</div>
            <div className={styles.infoText}>Un familiar puede apoyarte si lo prefieres.</div>
          </div>
        </div>
      </div>
    </>
  )
}
